import React, { useState, useEffect } from "react";
import { useParams, Link } from "react-router-dom";
import { 
  Calendar, 
  User, 
  ArrowLeft, 
  Share2, 
  Bookmark, 
  Check, 
  Clock, 
  Loader2, 
  Mail, 
  BookOpen
} from "lucide-react";
import { Button } from "@/components/ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import Header from "@/components/Header";
import Footer from "@/components/Footer";
import { SEO } from "@/components/SEO";
import { Article, fetchBlogArticleBySlugOrId } from "@/data/blog-articles";
import { useApiClient } from "@/hooks/use-api-client";
import { RichBulletinRenderer } from "@/pages/dashboard/boletines/components/RichBulletinRenderer";
import { toast } from "sonner";

const BlogArticle = () => {
  const { articleId } = useParams<{ articleId: string }>();
  const apiClient = useApiClient();

  const [article, setArticle] = useState<Article | null>(null);
  const [loading, setLoading] = useState(true);
  const [isSaved, setIsSaved] = useState(false);

  useEffect(() => {
    if (!articleId) return;
    let isMounted = true;
    setLoading(true);

    fetchBlogArticleBySlugOrId(articleId, apiClient)
      .then((data) => {
        if (isMounted) {
          setArticle(data);
        }
      })
      .catch((err) => {
        console.error("Error loading blog article detail:", err);
      })
      .finally(() => {
        if (isMounted) setLoading(false);
      });

    return () => {
      isMounted = false;
    };
  }, [articleId, apiClient]);

  const handleCopyLink = () => {
    navigator.clipboard.writeText(window.location.href);
    toast.success("Enlace del artículo copiado al portapapeles");
  };

  const handleEmailShare = () => {
    if (!article) return;
    const subject = encodeURIComponent(`Artículo Legal AI: ${article.title}`);
    const body = encodeURIComponent(`Te comparto este artículo jurídico relevante:

${article.title}

${window.location.href}`);
    window.location.href = `mailto:?subject=${subject}&body=${body}`;
  };

  const handleNativeShare = () => {
    if (navigator.share && article) {
      navigator.share({
        title: article.title,
        text: article.excerpt,
        url: window.location.href,
      }).catch(() => {});
    } else {
      handleCopyLink();
    }
  };

  const handleSave = () => {
    setIsSaved(!isSaved);
    toast.success(isSaved ? "Artículo removido de guardados" : "Artículo guardado en tu biblioteca");
  };

  if (loading) {
    return (
      <div className="min-h-screen flex flex-col">
        <Header />
        <main className="flex-grow container mx-auto px-4 py-24 flex flex-col items-center justify-center">
          <Loader2 className="h-12 w-12 animate-spin text-legal-blue mb-4" />
          <p className="text-gray-500 font-medium">Cargando artículo...</p>
        </main>
        <Footer />
      </div>
    );
  }

  if (!article) {
    return (
      <div className="min-h-screen flex flex-col">
        <SEO
          title="Artículo no encontrado | Legal AI"
          description="El artículo solicitado no está disponible o ha sido movido."
          type="article"
        />
        <Header />
        <main className="flex-grow container mx-auto px-4 py-20 text-center max-w-lg">
          <BookOpen className="w-16 h-16 text-gray-300 mx-auto mb-4" />
          <h1 className="text-3xl font-bold mb-3 text-legal-navy">Artículo no encontrado</h1>
          <p className="text-gray-600 mb-8">
            El boletín o artículo que estás buscando no existe o aún no ha sido publicado.
          </p>
          <Button asChild className="rounded-full btn-gradient">
            <Link to="/blog" className="flex items-center gap-2">
              <ArrowLeft size={16} />
              Volver al Blog
            </Link>
          </Button>
        </main>
        <Footer />
      </div>
    );
  }

  const articleStructuredData = {
    "@context": "https://schema.org",
    "@type": "BlogPosting",
    "mainEntityOfPage": {
      "@type": "WebPage",
      "@id": `https://miasistentelegalia.com/blog/${article.id}`
    },
    "headline": article.title,
    "description": article.excerpt,
    "image": [article.image],
    "datePublished": article.date,
    "dateModified": article.date,
    "author": {
      "@type": "Person",
      "name": article.author
    },
    "publisher": {
      "@type": "Organization",
      "name": "Mi Asistente Legal IA",
      "logo": {
        "@type": "ImageObject",
        "url": "https://miasistentelegalia.com/assets/logo-premium.png"
      }
    },
    "inLanguage": "es-CO"
  };

  const breadcrumbStructuredData = {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    "itemListElement": [
      {
        "@type": "ListItem",
        "position": 1,
        "name": "Inicio",
        "item": "https://miasistentelegalia.com/"
      },
      {
        "@type": "ListItem",
        "position": 2,
        "name": "Blog",
        "item": "https://miasistentelegalia.com/blog"
      },
      {
        "@type": "ListItem",
        "position": 3,
        "name": article.category,
        "item": "https://miasistentelegalia.com/blog"
      },
      {
        "@type": "ListItem",
        "position": 4,
        "name": article.title,
        "item": `https://miasistentelegalia.com/blog/${article.id}`
      }
    ]
  };

  return (
    <div className="min-h-screen flex flex-col bg-white">
      <SEO
        title={`${article.title} | Blog Legal AI`}
        description={article.excerpt}
        keywords={`${article.category.toLowerCase()}, derecho colombia, inteligencia artificial, ${article.title.toLowerCase()}`}
        image={article.image}
        type="article"
        url={`https://miasistentelegalia.com/blog/${article.id}`}
      />
      <script type="application/ld+json">
        {JSON.stringify(articleStructuredData)}
      </script>
      <script type="application/ld+json">
        {JSON.stringify(breadcrumbStructuredData)}
      </script>

      <Header />
      <main className="flex-grow">
        {/* Article Header */}
        <div className="bg-legal-navy text-white py-16 relative overflow-hidden">
          <div className="absolute inset-0 bg-gradient-to-r from-legal-navy via-slate-900 to-legal-navy opacity-90" />
          <div className="container mx-auto px-4 relative z-10">
            <div className="max-w-4xl mx-auto">
              <Button
                asChild
                variant="ghost"
                className="text-white/80 hover:text-white hover:bg-white/10 mb-6 rounded-full px-4 -ml-4">
                <Link to="/blog" className="flex items-center gap-2">
                  <ArrowLeft size={16} />
                  Volver al Blog
                </Link>
              </Button>
              
              <span className="inline-block px-3.5 py-1 bg-legal-gold/20 text-legal-gold font-semibold rounded-full text-xs uppercase tracking-wider mb-4 border border-legal-gold/30">
                {article.category}
              </span>
              
              <h1 className="text-3xl md:text-5xl font-bold mb-6 leading-tight">
                {article.title}
              </h1>

              <div className="flex flex-wrap items-center gap-6 text-sm text-white/80 border-t border-white/10 pt-6">
                <div className="flex items-center gap-2">
                  <User size={16} className="text-legal-gold" />
                  <span className="font-medium text-white">{article.author}</span>
                </div>
                <div className="flex items-center gap-2">
                  <Calendar size={16} className="text-legal-gold" />
                  <span>{article.date}</span>
                </div>
                <div className="flex items-center gap-2">
                  <Clock size={16} className="text-legal-gold" />
                  <span>{article.readTime} de lectura</span>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Article Main Image & Content */}
        <article className="py-12">
          <div className="container mx-auto px-4">
            <div className="max-w-4xl mx-auto">
              {article.image && (
                <div className="aspect-[21/9] rounded-2xl overflow-hidden mb-10 shadow-lg border border-gray-100">
                  <img
                    src={article.image}
                    alt={article.title}
                    className="w-full h-full object-cover"
                  />
                </div>
              )}

              {/* Rich Markdown Content */}
              <div className="prose prose-lg dark:prose-invert max-w-none">
                <RichBulletinRenderer content={article.content} />
              </div>
            </div>
          </div>
        </article>

        {/* Floating Action Bar */}
        <div className="border-t border-gray-200 py-6 bg-gray-50/50 sticky bottom-0 backdrop-blur-md">
          <div className="container mx-auto px-4">
            <div className="max-w-4xl mx-auto flex items-center justify-between">
              <Button
                asChild
                variant="outline"
                className="rounded-full border-gray-300 hover:bg-gray-100 text-gray-700">
                <Link to="/blog" className="flex items-center gap-2">
                  <ArrowLeft size={16} />
                  Ver más artículos
                </Link>
              </Button>

              <div className="flex items-center gap-3">
                <DropdownMenu>
                  <DropdownMenuTrigger asChild>
                    <Button variant="outline" className="rounded-full flex items-center gap-2 border-gray-300">
                      <Share2 size={16} />
                      <span className="hidden sm:inline">Compartir</span>
                    </Button>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent align="end">
                    <DropdownMenuItem onClick={handleNativeShare}>
                      <Share2 className="mr-2 h-4 w-4" />
                      Compartir nativo
                    </DropdownMenuItem>
                    <DropdownMenuItem onClick={handleEmailShare}>
                      <Mail className="mr-2 h-4 w-4" />
                      Enviar por Email
                    </DropdownMenuItem>
                    <DropdownMenuItem onClick={handleCopyLink}>
                      <Share2 className="mr-2 h-4 w-4" />
                      Copiar Enlace
                    </DropdownMenuItem>
                  </DropdownMenuContent>
                </DropdownMenu>

                <Button
                  variant={isSaved ? "default" : "outline"}
                  className="rounded-full flex items-center gap-2 border-gray-300"
                  onClick={handleSave}>
                  {isSaved ? <Check size={16} /> : <Bookmark size={16} />}
                  <span>{isSaved ? "Guardado" : "Guardar"}</span>
                </Button>
              </div>
            </div>
          </div>
        </div>
      </main>
      <Footer />
    </div>
  );
};

export default BlogArticle;
